import {
  Typography,
  Divider,
  Breadcrumbs,
  Link as MuiLink,
} from "@mui/material";
import { Link as RouterLink, useHistory, useParams } from "react-router-dom";
import { ROUTES } from "../../constants";
import { Helmet } from "react-helmet-async";
import { DynamicForm } from "../../dynamicForm/forms/DynamicForm";
import React from "react";
import { useDynamicForm } from "../../dynamicForm/hooks/useDynamicForm";
import {
  newWaterServiceConnectionFormConfig,
  adminWaterServiceConnectionFormConfig,
  CONNECTION_ROLE_FIELDS,
  TYPE_OF_CONNECTION_FIELDS,
} from "../../dynamicForm/formConfigs/newWaterServiceConnectionFormConfig";
import { usePendingWaterServiceConnections } from "./usePendingWaterServiceConnections";
import {
  ContentSkeleton,
  ErrorCard,
  PageContainer,
} from "../../dynamicForm/components/ui";
import PendingWaterServiceConnectionsList from "./PendingWaterServiceConnectionsList";
import { makeExactlyOneValidator } from "../../dynamicForm/lib/utils";

function validateWaterServiceConnection(data) {
  return [
    ...makeExactlyOneValidator(
      TYPE_OF_CONNECTION_FIELDS,
      "Select exactly one type of connection"
    )(data),
    ...makeExactlyOneValidator(
      CONNECTION_ROLE_FIELDS,
      "Select either Owner or Builder"
    )(data),
  ];
}

const IS_NEW = false;

const ADMIN_FORM_CONFIG = [
  ...adminWaterServiceConnectionFormConfig,
  ...newWaterServiceConnectionFormConfig,
];

// Form component that only runs when editing
function WaterServiceConnectionForm({ connectionId }) {
  const history = useHistory();
  const redirectPath = ROUTES.PENDING_WATER_SERVICE_CONNECTIONS_MANAGEMENT;
  const handleRedirect = () => history.push(redirectPath);

  const {
    isLoading: isLoadingFormData,
    error: formDataError,
    refetchFormData,
    refetchLookups,
    ...rest
  } = useDynamicForm({
    ndx: connectionId,
    isNewRecord: IS_NEW,
    config: ADMIN_FORM_CONFIG,
    endpoints: {
      submit: `/water-service-connection`,
      fetch: `/water-service-connection`,
    },
    handleRedirect,
    onSuccess: () => {},
    customValidate: validateWaterServiceConnection,
  });

  if (formDataError) {
    return (
      <ErrorCard
        onRetry={() =>
          Promise.all(
            [refetchFormData, refetchLookups].map((action) => action())
          )
        }
        isFetching={isLoadingFormData}
      />
    );
  }

  if (isLoadingFormData) {
    return <ContentSkeleton />;
  }

  return <DynamicForm config={ADMIN_FORM_CONFIG} {...rest} />;
}

export default function PendingWaterServiceConnectionPage() {
  const { id } = useParams();

  // Only fetch full list when showing the list (no id)
  const { data, isFetching, error, refetch } =
    usePendingWaterServiceConnections(!id);

  const pageTitle = id
    ? `Edit Water Service Connection #${id}`
    : "Pending Water Service Connections";

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>

      <Typography variant="h3" gutterBottom display="inline">
        {pageTitle}
      </Typography>
      <Breadcrumbs sx={{ mt: 4 }} aria-label="Breadcrumb">
        <MuiLink component={RouterLink} to={ROUTES.LANDING}>
          Home
        </MuiLink>
        <MuiLink component={RouterLink} to={ROUTES.PUBLIC_FORMS}>
          Forms
        </MuiLink>
        <Typography>{pageTitle}</Typography>
      </Breadcrumbs>
      <Divider sx={{ my: 6 }} />

      <PageContainer>
        {error ? (
          <ErrorCard onRetry={refetch} isFetching={isFetching} />
        ) : isFetching ? (
          <ContentSkeleton />
        ) : id ? (
          <WaterServiceConnectionForm connectionId={id} />
        ) : (
          <PendingWaterServiceConnectionsList connections={data || []} />
        )}
      </PageContainer>
    </>
  );
}
